/**
 * The marketing site's own furniture, rebuilt for the campaign pages.
 *
 * These screens read as a different product from alchemiq.ai: the campaign
 * ended each page on a lone outlined button and a one-word footer, where the
 * site closes on a full-bleed navy band and a four-column footer. Same brand,
 * two different impressions.
 *
 * All three sections are FULL-BLEED, so they render outside `.cmp-main`'s
 * column — the band losing its background at the container edge is what made
 * the original CTA look like an afterthought rather than a destination.
 *
 * Every word here comes from `SITE` in `audienceCopy.js`, which is verbatim
 * alchemiq.ai. Nothing on these strips is newly written.
 */

import React from 'react';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { SITE } from './audienceCopy';

/**
 * The site's closing band: navy, centred, one orange primary and one
 * outlined companion.
 *
 * The demo action stays INSIDE the campaign (`/c/:audience/demo`) rather than
 * pointing at the site's contact form — the campaign form is prefilled with
 * what we already know about the visitor, and it records a demo request
 * against their visit. "See customer results" leaves for the site, because
 * there is no campaign equivalent.
 */
export function CampaignCta({ audience, onRequestDemo }) {
  // The band renders on every page, so it cannot depend on every caller
  // remembering to pass a handler — without this, a page that forgot one
  // would show a button that silently does nothing.
  const requestDemo = onRequestDemo
    || (() => { window.location.href = `/c/${audience || 'journalist'}/demo`; });
  return (
    <section className="cmp-cta-band">
      <div className="cmp-cta-inner">
        <h2 className="cmp-cta-title">{SITE.ctaTitle}</h2>
        <p className="cmp-cta-body">{SITE.ctaBody}</p>
        <div className="cmp-cta-actions">
          <button type="button" className="cmp-btn cmp-btn--primary" onClick={requestDemo}>
            {SITE.ctaPrimary}
          </button>
          <a
            className="cmp-btn cmp-btn--onnavy"
            href={SITE.ctaSecondaryHref}
            target="_blank"
            rel="noreferrer"
          >
            {SITE.ctaSecondary}
          </a>
        </div>
      </div>
      {/* Audience is carried only so a caller can key this section per route
          later; the site's band is deliberately the same for everyone. */}
      <span hidden data-audience={audience || ''} />
    </section>
  );
}

/** The site's results section. Published claims, reproduced, not restated. */
export function CampaignResults() {
  return (
    <section className="cmp-results">
      <div className="cmp-results-inner">
        <p className="cmp-eyebrow cmp-eyebrow--orange">{SITE.resultsEyebrow}</p>
        <h2 className="cmp-results-title">{SITE.resultsTitle}</h2>
        <div className="cmp-results-grid">
          {SITE.results.map((r) => (
            <div key={r.figure + r.label}>
              <p className="cmp-results-figure">{r.figure}</p>
              <p className="cmp-results-label">{r.label}</p>
              <p className="cmp-results-body">{r.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * The plain variant's footer: one line, and nothing else.
 *
 * Or's mockup ended on a single centred "Alchemiq" and that is the whole
 * brief here — a four-column sitemap under a page whose only job is a brief
 * box is the opposite of plain. Navigation is not lost: FAQ and Pricing are
 * in the header on every screen.
 */
export function CampaignFooterMinimal() {
  return (
    <footer className="cmp-footer cmp-footer--minimal">
      <div className="cmp-footer-minimal-inner">Alchemiq</div>
    </footer>
  );
}

/**
 * The site's footer, with one column the site does not have: the campaign's
 * own pages. A visitor who reaches the bottom of the FAQ should be able to
 * get to Pricing without scrolling back up.
 */
export function CampaignFooter({ audience }) {
  return (
    <footer className="cmp-footer">
      <div className="cmp-footer-inner">
        <div className="cmp-footer-brand-col">
          <img
            src="/textOnlyLogoOrr-01.png"
            alt="Alchemiq"
            className="cmp-footer-logo"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <p className="cmp-footer-blurb">{SITE.footerBlurb}</p>
        </div>

        {audience && (
          <div>
            <p className="cmp-footer-heading">Your campaign</p>
            <ul className="cmp-footer-list">
              <li><a href={`/c/${audience}`}>Your opportunities</a></li>
              <li><a href={`/c/${audience}/faq`}>FAQ</a></li>
              <li><a href={`/c/${audience}/pricing`}>Pricing</a></li>
              <li><a href={`/c/${audience}/demo`}>Request a demo</a></li>
            </ul>
          </div>
        )}

        <div>
          <p className="cmp-footer-heading">Alchemiq</p>
          <ul className="cmp-footer-list">
            {SITE.footerLinks.map((l) => (
              <li key={l.href}>
                <a href={l.href} target="_blank" rel="noreferrer">{l.label}</a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="cmp-footer-heading">Get in touch</p>
          <ul className="cmp-footer-list">
            <li>
              <a href={`mailto:${SITE.supportEmail}`} className="cmp-footer-mail">
                <MailOutlineIcon style={{ fontSize: 14 }} aria-hidden />
                {SITE.supportEmail}
              </a>
            </li>
          </ul>
          <p className="cmp-footer-heading cmp-footer-heading--spaced">Follow us</p>
          <ul className="cmp-footer-list">
            {SITE.social.map((l) => (
              <li key={l.href}>
                <a href={l.href} target="_blank" rel="noreferrer">{l.label}</a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="cmp-footer-bar">
        <div className="cmp-footer-bar-inner">
          <span>{SITE.copyright}</span>
          <nav className="cmp-footer-legal">
            {SITE.legal.map((l) => (
              <a key={l.href} href={l.href} target="_blank" rel="noreferrer">{l.label}</a>
            ))}
          </nav>
        </div>
      </div>
    </footer>
  );
}
